import {useMemo} from "react";
import {EnsLevelDto, EnsNotification, EnsRecipient} from "../../../store/features/ens/types";

interface Props {
    levels: EnsLevelDto[];
    recipients: EnsRecipient[];
    notifications: EnsNotification[];
}

interface Bucket {
    confirmed: number;
    active: number;
    escalated: number;
    failed: number;
}

const channelShortLabel = (channel: EnsLevelDto['channel']): string => {
    switch (channel) {
        case 'PUSH': return 'Push';
        case 'INTERNAL_CALL': return 'Internal';
        case 'SMS': return 'SMS';
        case 'PSTN': return 'Phone';
        case 'WHATSAPP': return 'WhatsApp';
    }
};

const emptyBucket = (): Bucket => ({confirmed: 0, active: 0, escalated: 0, failed: 0});

export const EscalationHistogram = ({levels, recipients, notifications}: Props) => {
    const {buckets, max} = useMemo(() => {
        const n = Math.max(1, levels.length);
        const result: Bucket[] = Array.from({length: n}, emptyBucket);
        notifications.forEach((notif) => {
            const idx = Math.min(Math.max(notif.levelSequence - 1, 0), n - 1);
            const bucket = result[idx];
            switch (notif.status) {
                case 'CONFIRMED':
                    bucket.confirmed += 1;
                    break;
                case 'PENDING':
                case 'DISPATCH_REQUESTED':
                case 'DISPATCHED':
                case 'DELIVERED':
                    bucket.active += 1;
                    break;
                case 'TIMEOUT':
                case 'CANCELLED':
                    bucket.escalated += 1;
                    break;
                case 'FAILED':
                    bucket.failed += 1;
                    break;
            }
        });
        const maxStack = Math.max(1, ...result.map((b) => b.confirmed + b.active + b.escalated + b.failed));
        return {buckets: result, max: maxStack};
    }, [levels, notifications]);

    const confirmedTotal = recipients.filter((r) => r.status === 'CONFIRMED').length;
    const targetTotal = recipients.length;

    return (
        <div className="border-b border-line px-5 py-4">
            <div className="mb-3 flex items-end justify-between">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
                    Escalation distribution
                </div>
                <div className="text-xs text-ink-muted">
                    {confirmedTotal}/{targetTotal} confirmed
                </div>
            </div>
            <div className="flex items-end gap-2">
                {levels.map((level, idx) => {
                    const bucket = buckets[idx] ?? emptyBucket();
                    const stackTotal = bucket.confirmed + bucket.active + bucket.escalated + bucket.failed;
                    const tooltipParts: string[] = [];
                    if (bucket.confirmed) tooltipParts.push(`${bucket.confirmed} confirmed`);
                    if (bucket.active) tooltipParts.push(`${bucket.active} active`);
                    if (bucket.escalated) tooltipParts.push(`${bucket.escalated} escalated`);
                    if (bucket.failed) tooltipParts.push(`${bucket.failed} failed`);
                    const tooltip = tooltipParts.join(' · ') || 'No notifications yet';
                    return (
                        <div key={level.id} className="flex flex-1 flex-col items-center gap-1.5">
                            <div className="text-sm font-semibold text-ink">{stackTotal}</div>
                            <div
                                className="flex h-[60px] w-full flex-col-reverse overflow-hidden rounded bg-surface-muted"
                                title={tooltip}
                            >
                                {bucket.confirmed > 0 && (
                                    <div
                                        className="bg-success"
                                        style={{height: `${(bucket.confirmed / max) * 100}%`}}
                                    />
                                )}
                                {bucket.active > 0 && (
                                    <div
                                        className="bg-brand"
                                        style={{height: `${(bucket.active / max) * 100}%`}}
                                    />
                                )}
                                {bucket.escalated > 0 && (
                                    <div
                                        className="bg-fill-subtle"
                                        style={{height: `${(bucket.escalated / max) * 100}%`}}
                                    />
                                )}
                                {bucket.failed > 0 && (
                                    <div
                                        className="bg-danger"
                                        style={{height: `${(bucket.failed / max) * 100}%`}}
                                    />
                                )}
                            </div>
                            <div className="text-[11px] font-semibold text-ink-secondary">L{level.sequenceNo}</div>
                            <div className="text-[10px] text-ink-muted">{channelShortLabel(level.channel)}</div>
                        </div>
                    );
                })}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px] text-ink-muted">
                <span className="inline-flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-sm bg-success"/> Confirmed
                </span>
                <span className="inline-flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-sm bg-brand"/> Active
                </span>
                <span className="inline-flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-sm bg-fill-subtle"/> Escalated
                </span>
                <span className="inline-flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-sm bg-danger"/> Failed
                </span>
            </div>
        </div>
    );
};
